import { useQuery } from '@tanstack/react-query';

import { urlUnfurlQueryOptions } from '@/entities/url-unfurl';

import { isWebSource, type Source } from './sourcePanel.types';

interface UnfurledSource {
  source: Source;
  /** True only while a web source is waiting on its first unfurl. */
  isLoading: boolean;
}

/**
 * Overlays what a publisher says about a cited page onto the record the citation gave us.
 *
 * The citation carries only an anchor label and a hostname, so the panel opens on those and
 * fills in once the page answers. A publisher that blocks us leaves the record untouched,
 * which is the same view the panel rendered before this existed.
 */
export const useUnfurledSource = (source: Source): UnfurledSource => {
  const isWeb = isWebSource(source);
  const { data, isLoading } = useQuery(urlUnfurlQueryOptions(isWeb ? source.url : undefined));

  if (!isWeb) return { source, isLoading: false };

  // `resolved` is false for a page that answered with nothing a publisher curated. Its title
  // and description are then scraped leftovers, so the citation's own wording stands instead.
  const unfurled = data?.resolved ? data : undefined;

  return {
    source: {
      ...source,
      site: unfurled?.siteName ?? source.site,
      title: unfurled?.title ?? source.title,
      subject: unfurled?.description ?? source.subject,
    },
    isLoading: Boolean(source.url) && isLoading,
  };
};
